import type { UpgradeEffect, Requirement } from './types';

/**
 * A challenge: opt in to a penalty modifier, then earn `target` money WHILE it's
 * active to claim a permanent reward. No reset — the "earn during" goal keeps it
 * meaningful regardless of prior progress.
 */
export interface ChallengeDef {
  id: string;
  name: string;
  icon: string;
  description: string;
  /** Applied as an active modifier while the challenge runs. */
  penalty: UpgradeEffect;
  /** Money to earn during the challenge to complete it. */
  target: number;
  /** Permanent multiplier granted on completion. */
  reward: UpgradeEffect;
  requires?: Requirement;
}

export const CHALLENGES: readonly ChallengeDef[] = [
  {
    id: 'brownout', name: 'Unterspannung', icon: '🔌',
    description: 'Energie ×0,35. Erreiche das Ziel mit knappem Netz.',
    penalty: { type: 'energyMult', value: 0.35 }, target: 1e7,
    reward: { type: 'energyMult', value: 1.25 }, requires: { lifetimeEarnings: 1e6 },
  },
  {
    id: 'recession', name: 'Rezession', icon: '📉',
    description: 'Verkaufswert ×0,4. Kämpf dich durch einen schlechten Markt.',
    penalty: { type: 'valueMult', value: 0.4 }, target: 1e8,
    reward: { type: 'valueMult', value: 1.3 }, requires: { lifetimeEarnings: 1e7 },
  },
  {
    id: 'handcramp', name: 'Handkrampf', icon: '✋',
    description: 'Klickkraft ×0,2. Lass die Automatik arbeiten.',
    penalty: { type: 'clickMult', value: 0.2 }, target: 1e9,
    reward: { type: 'autoMult', value: 1.3 }, requires: { lifetimeEarnings: 1e8 },
  },
  {
    id: 'skeletoncrew', name: 'Notbesetzung', icon: '💀',
    description: 'Arbeiter ×0,3. Verlass dich auf Maschinen.',
    penalty: { type: 'workerMult', value: 0.3 }, target: 1e10,
    reward: { type: 'workerMult', value: 1.4 }, requires: { lifetimeEarnings: 1e9 },
  },
  {
    id: 'cavein', name: 'Tiefer Einsturz', icon: '🪨',
    description: 'Gesamter Abbau ×0,5. Zeig, dass du trotzdem gräbst.',
    penalty: { type: 'autoMult', value: 0.5 }, target: 1e11,
    reward: { type: 'globalMult', value: 1.25 }, requires: { depth: 6 },
  },
] as const;

export const CHALLENGE_BY_ID: Record<string, ChallengeDef> = Object.fromEntries(
  CHALLENGES.map((c) => [c.id, c]),
);
